// A client's representatives.
//
// The people at the client's end: the site contact who is not the person who
// signed the contract, the foreman on one subdivision, the after-hours number.
// A job points at one of these (round 34) so a crew can dial the right person
// from the job screen instead of hunting through a description field.
//
// Removing one ARCHIVES it, because a job that named this person has to keep
// naming them. That is a server rule (migration 033); the button here says
// "Remove" because that is what the customer is doing, and the confirm says
// what actually happens.
import { useState } from 'react';
import { ApiError } from '../../api';
import { scheduling, type ClientRep } from '../../schedulingApi';
import { Banner, Field } from '../../ui';
import { CardList, RowCard } from '../ui/RowCard';
import { Modal } from '../ui/Modal';

export function RepsPanel({
  clientId,
  reps,
  onChanged,
}: {
  clientId: string;
  reps: ClientRep[];
  onChanged: () => Promise<void>;
}) {
  const [editing, setEditing] = useState<ClientRep | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function remove(rep: ClientRep) {
    if (
      !window.confirm(
        `Remove ${rep.name}? Any job that already names them keeps naming them; they just stop ` +
          'being offered on new ones.'
      )
    ) {
      return;
    }
    setBusy(rep.id);
    setError(null);
    try {
      await scheduling.removeClientRep(clientId, rep.id);
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove that.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="panel">
      <div className="row mb-3">
        <h2 className="!mb-0">
          Representatives
          {reps.length > 0 && <span className="muted"> ({reps.length})</span>}
        </h2>
        <span className="spacer" />
        <button type="button" className="btn-secondary btn-sm" onClick={() => setEditing('new')}>
          Add representative
        </button>
      </div>

      {error && <Banner kind="error">{error}</Banner>}

      {reps.length === 0 ? (
        <p className="muted">
          Nobody yet. Add the people at this client&rsquo;s end: a job can then name one of them,
          so the crew knows who to ring.
        </p>
      ) : (
        <>
          <CardList>
            {reps.map((rep) => (
              <RowCard
                key={rep.id}
                title={rep.name}
                actions={
                  <>
                    {rep.phone && (
                      <a className="row-action" href={`tel:${rep.phone.replace(/[^+\d]/g, '')}`}>
                        {rep.phone}
                      </a>
                    )}
                    <button type="button" className="row-action" onClick={() => setEditing(rep)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="row-action-danger"
                      disabled={busy === rep.id}
                      onClick={() => void remove(rep)}
                    >
                      Remove
                    </button>
                  </>
                }
              >
                {rep.email && <div>{rep.email}</div>}
                {rep.notes && <div>{rep.notes}</div>}
              </RowCard>
            ))}
          </CardList>

          {/* Full-bleed inside the panel (the job page's Schedule idiom), not
              `.table-wrap` — that is a card, and a card inside a panel grows
              its own scrollbar beside the page's, which is the round-35
              client-page bug. */}
          <div className="-mx-6 overflow-x-auto border-y border-gray-200 max-sm:hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Phone</th>
                  <th scope="col">Email</th>
                  <th scope="col">Notes</th>
                  <th scope="col" className="w-px">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {reps.map((rep) => (
                  <tr key={rep.id}>
                    <td className="table-cell">{rep.name}</td>
                    <td className="table-cell">
                      {rep.phone ? (
                        <a className="table-link" href={`tel:${rep.phone.replace(/[^+\d]/g, '')}`}>
                          {rep.phone}
                        </a>
                      ) : (
                        <span className="table-muted">—</span>
                      )}
                    </td>
                    <td className="table-cell">{rep.email ?? <span className="table-muted">—</span>}</td>
                    <td className="table-cell">{rep.notes ?? <span className="table-muted">—</span>}</td>
                    <td className="table-cell w-px whitespace-nowrap">
                      <button type="button" className="row-action" onClick={() => setEditing(rep)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="row-action-danger"
                        disabled={busy === rep.id}
                        onClick={() => void remove(rep)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {editing && (
        <RepModal
          clientId={clientId}
          rep={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await onChanged();
          }}
        />
      )}
    </section>
  );
}

/**
 * Add or edit one representative.
 *
 * Exported because the job screen opens the same dialog: adding a rep while
 * filling in a job is the case ZT built it for, and two dialogs for one job
 * would be two sets of validation to keep in step.
 */
export function RepModal({
  clientId,
  rep,
  onClose,
  onSaved,
}: {
  clientId: string;
  rep: ClientRep | null;
  onClose: () => void;
  /** Given the saved rep, so a caller can select it straight away. */
  onSaved: (saved: ClientRep) => void | Promise<void>;
}) {
  const [name, setName] = useState(rep?.name ?? '');
  const [phone, setPhone] = useState(rep?.phone ?? '');
  const [email, setEmail] = useState(rep?.email ?? '');
  const [notes, setNotes] = useState(rep?.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFieldErrors({});
    try {
      const body = { name, phone, email, notes };
      const result = rep
        ? await scheduling.updateClientRep(clientId, rep.id, body)
        : await scheduling.createClientRep(clientId, body);
      await onSaved(result.rep);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFieldErrors(err.fields);
      } else {
        setError('Could not save that. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={rep ? 'Edit representative' : 'Add representative'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="rep-form" className="btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {/* legacy-page so the `.row` classes resolve inside the portal. */}
      <form id="rep-form" className="legacy-page" onSubmit={save} noValidate>
        {error && <Banner kind="error">{error}</Banner>}
        <Field label="Name" name="rep-name" value={name} onChange={setName} error={fieldErrors.name} />
        <div className="row">
          <Field
            label="Phone"
            name="rep-phone"
            value={phone}
            onChange={setPhone}
            error={fieldErrors.phone}
            required={false}
            inputMode="tel"
          />
          <Field
            label="Email"
            name="rep-email"
            type="email"
            value={email}
            onChange={setEmail}
            error={fieldErrors.email}
            required={false}
            autoCapitalize="none"
            spellCheck={false}
            inputMode="email"
          />
        </div>
        <label htmlFor="rep-notes">
          Notes
          <textarea
            id="rep-notes"
            rows={2}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </label>
      </form>
    </Modal>
  );
}
